/**
 * API client for the Daily Photo List (Phase 5G).
 *
 * Wraps the /photo-list/* endpoints with typed fetchers.
 */
import { API } from "../config/api"

// --- Types matching backend _run_to_dict output ---

export interface PhotoListSheetCounts {
  toyota_new: number
  toyota_used: number
  lexus_new: number
  lexus_used: number
  mazda_new: number
  kia_new: number
  mazda_kia_used: number
  dodge_new: number
  dodge_used: number
  fernley_new: number
  fernley_used: number
  lee_bros_used: number
}

export interface PhotoListRun {
  id: number
  started_at: string | null
  completed_at: string | null
  status: string
  initiated_by_user_id: number
  total_count: number
  error_message: string | null
  /** Newline-joined warnings harvested from a successful run's stdout
   * ("No Dealer Daily Toyota file found.") — null for a clean run. Rendered
   * as a banner so a degraded list never reads as vendor truth. */
  degradation_notes: string | null
  sheet_counts: PhotoListSheetCounts
}

// --- Helpers ---

function getToken(): string {
  return localStorage.getItem("invi_token") ?? ""
}

async function authFetch(url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    // FastAPI's {"detail": "..."} as the plain sentence it is — the settings
    // page showed operators the raw envelope (review, 2026-09-25).
    const body = await res.text()
    let message = body || `HTTP ${res.status}`
    try {
      const parsed = JSON.parse(body)
      if (typeof parsed?.detail === "string") message = parsed.detail
    } catch {
      // Not JSON: keep the text.
    }
    throw new Error(message)
  }
  return res
}

// --- Vehicle type matching backend _vehicle_to_dict ---

export interface PhotoListVehicle {
  id: number
  sheet: string
  vin: string | null
  stock: string | null
  rooftop: string | null
  vehicle_type: string | null
  age_days: number | null
  sale_status_raw: string | null
  recon_status_raw: string | null
  cmpgn: string | null
  transit: string | null
  stargard: string | null
  shot_status: string | null
  photos_count: number | null
  year: number | null
  make: string | null
  model: string | null
  trim: string | null
  color_ext: string | null
  print_notes: string | null
  /** Newest operator note on this car. Shown on screen and painted into the
   * Excel export's Notes column; only the print view stays blank for pens. */
  latest_note: string | null
  /** The photographer's four-circle state, already stint-checked server-side
   * (a status from a previous stock reads as null). */
  progress_status: ProgressStatus | null
  /** This car's colors from its group's highlight rules, keyed by column
   * ("vin", "stargard", ...) — worked out on the server; the page only
   * paints them (utils/photoListStyling.ts). */
  cell_colors: Partial<Record<string, PhotoListCellColor>>
  /** Columns whose text is bold black (Toyota's STOP). */
  bold_cells: string[]
  /** Someone added this car by hand to today's list (Ian, 2026-10-11) — it
   * leads its sheet, wears an ADDED tag, and is the only kind of row that
   * can be removed. */
  added_by_hand: boolean
}

/** Body for POST /photo-list/added-vehicles — mirrors AddedVehicleRequest in
 * backend/schemas.py. `sheet` is the sheet slug ("toyota_used"). */
export interface AddedVehicleRequest {
  vin: string
  sheet: string
  /** Required (Ian, 2026-10-12): circles and automatic Done work from it. */
  stock: string
  year?: number | null
  make?: string | null
  model?: string | null
  color_ext?: string | null
  stargard?: string | null
}

/** Mirrors AddedVehicleResponse in backend/schemas.py. */
export interface AddedVehicleResponse {
  id: number
  run_id: number
  sheet: string
  vin: string
}

/** Mirrors PAINT_COLORS in backend/services/photo_list_highlighting.py. */
export type PhotoListCellColor =
  | "green"
  | "yellow"
  | "dark yellow"
  | "orange"
  | "blue"
  | "red"
  | "light grey"
  | "dark grey"

/** Mirrors ALLOWED_VEHICLE_PROGRESS in backend/routers/photo_list.py. */
export type ProgressStatus =
  | "no_keys"
  | "in_progress"
  | "issue"
  | "cancelled"
  | "done"

// --- File status types (matches backend check_file_requirements response) ---

export interface PhotoListFileEntry {
  label: string
  status: "present" | "missing" | "stale"
  filename?: string
  age_seconds?: number
  fresh?: boolean
  required: boolean
  stale_count?: number
  files?: Array<{
    filename: string
    rooftop: string
    fresh: boolean
    age_seconds: number
  }>
  coverage?: string[]
}

export interface DealerConnectRooftopStatus {
  label: string
  dealer_code: string
  filename: string | null
  age_seconds: number | null
  fresh: boolean
  status: "present" | "stale" | "missing"
}

export interface DealerConnectStatus {
  label: string
  required: boolean
  rooftops: DealerConnectRooftopStatus[]
}

export interface PhotoListFileStatus {
  all_required_present: boolean
  all_required_fresh: boolean
  can_run: boolean
  stargard_coverage: string[]
  stargard_missing: string[]
  files: Record<string, PhotoListFileEntry>
  /** Dealer Connect uses a rooftop-based shape, separate from files. */
  dealer_connect?: DealerConnectStatus
}

// --- Endpoints ---

function groupParam(dealerGroupId?: number | null, sep: "?" | "&" = "?"): string {
  return dealerGroupId != null ? `${sep}dealer_group_id=${dealerGroupId}` : ""
}

export async function getLatestPhotoListRun(
  dealerGroupId?: number | null,
): Promise<PhotoListRun> {
  const res = await authFetch(
    `${API.baseUrl}/photo-list/runs/latest${groupParam(dealerGroupId)}`,
  )
  return res.json()
}

export async function getPhotoListRun(runId: number): Promise<PhotoListRun> {
  const res = await authFetch(`${API.baseUrl}/photo-list/runs/${runId}`)
  return res.json()
}

export async function listPhotoListRuns(
  limit = 20,
  dealerGroupId?: number | null,
): Promise<PhotoListRun[]> {
  const res = await authFetch(
    `${API.baseUrl}/photo-list/runs?limit=${limit}${groupParam(dealerGroupId, "&")}`,
  )
  return res.json()
}

export async function fetchPhotoListVehicles(
  runId: number,
  sheetSlug: string,
): Promise<PhotoListVehicle[]> {
  const res = await authFetch(
    `${API.baseUrl}/photo-list/runs/${runId}/vehicles?sheet=${sheetSlug}`,
  )
  const data = await res.json()
  return data.vehicles ?? data
}

export async function fetchPhotoListFileStatus(
  dealerGroupId?: number | null,
): Promise<PhotoListFileStatus> {
  const res = await authFetch(
    `${API.baseUrl}/photo-list/file-status${groupParam(dealerGroupId)}`,
  )
  const data = await res.json()
  // Extract dealer_connect from files (rooftop-based shape) to top-level
  const { dealer_connect, ...restFiles } = data.files ?? {}
  return {
    ...data,
    files: restFiles,
    dealer_connect: dealer_connect ?? undefined,
  }
}

export async function triggerPhotoListRun(
  dealerGroupId?: number | null,
): Promise<PhotoListRun> {
  const res = await authFetch(
    `${API.baseUrl}/photo-list/run${groupParam(dealerGroupId)}`,
    {
      method: "POST",
    },
  )
  return res.json()
}

// --- Preferences ---

export interface PhotoListPreferences {
  selected_sheets: string[]
  sheet_order: string[]
}

export async function fetchPhotoListPreferences(): Promise<PhotoListPreferences> {
  const res = await authFetch(`${API.baseUrl}/photo-list/preferences`)
  return res.json()
}

export async function updatePhotoListPreferences(
  prefs: PhotoListPreferences,
): Promise<PhotoListPreferences> {
  const res = await authFetch(`${API.baseUrl}/photo-list/preferences`, {
    method: "PUT",
    body: JSON.stringify(prefs),
  })
  return res.json()
}

// --- Vehicle notes ---
// Same vehicle_notes table as the audit worklist, but each page shows only
// its own thread (the backend filters by source). Keyed to (dealer group,
// VIN), add-only, survives the photo list's daily regeneration.

export interface VehicleNoteEntry {
  id: number
  vin: string
  problem_type: string | null
  body: string
  author_name: string | null
  created_at: string
  /** Stock the car carried when written; a mismatch vs. the current stock
   * marks a note from a previous stay. NULL reads as current. */
  stock: string | null
}

export async function listVehicleNotes(
  vin: string,
  dealerGroupId?: number | null,
): Promise<VehicleNoteEntry[]> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListVehicleNotes(vin)}${groupParam(dealerGroupId)}`,
  )
  const data = await res.json()
  return data.notes ?? []
}

export async function addVehicleNote(
  vin: string,
  body: string,
  stock: string | null,
  dealerGroupId?: number | null,
): Promise<VehicleNoteEntry> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListVehicleNotes(vin)}${groupParam(dealerGroupId)}`,
    { method: "POST", body: JSON.stringify({ body, stock }) },
  )
  return res.json()
}

export async function setVehicleProgress(
  vin: string,
  status: ProgressStatus | null,
  stock: string | null,
  dealerGroupId?: number | null,
): Promise<void> {
  await authFetch(
    `${API.baseUrl}${API.endpoints.photoListVehicleProgress(vin)}${groupParam(dealerGroupId)}`,
    { method: "PUT", body: JSON.stringify({ status, stock }) },
  )
}

// --- Cars added by hand ---

export async function addVehicleToList(
  request: AddedVehicleRequest,
  dealerGroupId?: number | null,
): Promise<AddedVehicleResponse> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListAddedVehicles}${groupParam(dealerGroupId)}`,
    { method: "POST", body: JSON.stringify(request) },
  )
  return res.json()
}

export async function removeAddedVehicle(
  vehicleId: number,
  dealerGroupId?: number | null,
): Promise<void> {
  await authFetch(
    `${API.baseUrl}${API.endpoints.photoListAddedVehicle(vehicleId)}${groupParam(dealerGroupId)}`,
    { method: "DELETE" },
  )
}

// --- XLSX export ---

export async function downloadPhotoListXlsx(
  selectedSheets: string[],
  sheetOrder: string[],
  dealerGroupId?: number | null,
): Promise<Blob> {
  const res = await fetch(`${API.baseUrl}/photo-list/export${groupParam(dealerGroupId)}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify({
      selected_sheets: selectedSheets,
      sheet_order: sheetOrder,
    }),
  })
  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `Export failed: ${res.status}`)
  }
  return res.blob()
}

// --- File upload ---

export interface PhotoListUploadResult {
  filename: string
  path: string
  size: number
}

export async function deletePhotoListFile(
  filename: string,
  dealerGroupId?: number | null,
): Promise<void> {
  const res = await fetch(
    `${API.baseUrl}/photo-list/files/${encodeURIComponent(filename)}${groupParam(dealerGroupId)}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
    },
  )
  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `Delete failed: ${res.status}`)
  }
}

export async function uploadVendorFile(
  file: File,
  options?: { stargardRooftop?: string },
  dealerGroupId?: number | null,
): Promise<PhotoListUploadResult> {
  const formData = new FormData()
  formData.append("file", file)
  if (options?.stargardRooftop) {
    formData.append("stargard_rooftop", options.stargardRooftop)
  }

  const res = await fetch(`${API.baseUrl}/photo-list/upload${groupParam(dealerGroupId)}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${getToken()}`,
    },
    body: formData,
  })

  if (!res.ok) {
    const body = await res.text()
    throw new Error(body || `Upload failed: ${res.status}`)
  }
  return res.json()
}

// ---------------------------------------------------------------------------
// Photo List Settings (operator-editable per-group config)
// ---------------------------------------------------------------------------
// Mirrors backend/schemas.py "Photo List Settings" section field-for-field.
// GET serves the EFFECTIVE config: database rows when the group is customized,
// rows synthesized from the code constants (id: null) when it isn't — the
// *_source flags say which, per cluster. Mutations exist only for real rows.

export type PhotoListHighlightColor = "green" | "yellow" | "orange" | "red"

export interface PhotoListLot {
  id: number | null
  name: string
}

export interface PhotoListStargardAlias {
  id: number | null
  raw_text: string
  lot_id: number | null
  lot_name: string
  /** Which Stargard source file this translation applies to ("Toyota",
   * "Dodge", ...); "" = any file. File-scoped rows win for their file. */
  stargard_rooftop: string
}

export interface PhotoListReconStep {
  id: number | null
  name: string
  highlight_color: PhotoListHighlightColor | null
  priority_score: number
  excluded_from_list: boolean
}

export interface PhotoListNewLotPriority {
  id: number | null
  rooftop_name: string
  rooftop_label: string
  lot_id: number | null
  lot_name: string
  score: number
}

export interface PhotoListUsedLotBoost {
  id: number | null
  rooftop_name: string
  rooftop_label: string
  lot_id: number | null
  lot_name: string
  boost: number
}

export interface PhotoListRooftopOption {
  name: string
  label: string
}

export interface PhotoListSettings {
  stargard_source: "database" | "code_defaults"
  recon_source: "database" | "code_defaults"
  lot_priority_source: "database" | "code_defaults"
  lots: PhotoListLot[]
  stargard_aliases: PhotoListStargardAlias[]
  recon_steps: PhotoListReconStep[]
  new_lot_priorities: PhotoListNewLotPriority[]
  used_lot_boosts: PhotoListUsedLotBoost[]
  rooftop_options: PhotoListRooftopOption[]
  /** The six Stargard source files, in coverage-panel order — the aliases
   * panel's tabs and the vocabulary of stargard_rooftop scopes. */
  stargard_file_options: string[]
  /** The latest run's feed text that matched no rule — pre-filtered against
   * the current rules server-side, so it self-clears as rules are added. */
  unrecognized: {
    recon_steps: Array<{ name: string; cars: number }>
  }
  /** What each store's Stargard file said on the latest successful run,
   * grouped by file and resolved against the current rules — the Address
   * Aliases layout. null = the group has no successful run yet. */
  stargard_seen: PhotoListStargardSeen | null
  /** The color rules, top first; "code_defaults" = the group has none of its
   * own and uses the generic starter rules (ids null). All three are absent
   * from a server deployed before color rules existed. */
  highlight_source?: PhotoListHighlightSource
  highlight_rules?: PhotoListHighlightRule[]
  /** The rule editor's choices (the server's own vocabulary). */
  highlight_options?: PhotoListHighlightOptions
}

export type PhotoListHighlightSource = "database" | "code_defaults"

/** One color rule (backend services/photo_list_highlighting.py): when
 * `field` [condition], paint `columns` `color`, only for `vehicle_type`
 * (null = both) at `rooftops` (empty = every store). Rules run top to
 * bottom; a later rule's color wins. */
export interface PhotoListHighlightRule {
  id: number | null
  position: number
  enabled: boolean
  field: string
  condition: "is_one_of" | "is_not_blank"
  /** Typed values an is_one_of rule matches (ignores capitals and outer
   * spaces); empty when `recon_color` supplies them. */
  values: string[]
  /** The recon steps given this color under Recon Steps. */
  recon_color: PhotoListHighlightColor | null
  lot_requirement: "new_walking_order" | "used_front_lot" | null
  columns: string[]
  color: PhotoListCellColor
  vehicle_type: "NEW" | "USED" | null
  rooftops: string[]
  /** The rule's own column turns bold black (Toyota's STOP). */
  bold_value: boolean
}

/** A rule as the editor sends it — the server fills in id and position. */
export type PhotoListHighlightRuleInput = Omit<PhotoListHighlightRule, "id" | "position">

export interface PhotoListHighlightOptions {
  fields: Array<{ key: string; label: string }>
  conditions: Array<{ key: PhotoListHighlightRule["condition"]; label: string }>
  lot_requirements: Array<{ key: NonNullable<PhotoListHighlightRule["lot_requirement"]>; label: string }>
  colors: PhotoListCellColor[]
  recon_colors: PhotoListHighlightColor[]
  vehicle_types: Array<"NEW" | "USED">
}

/** Every write returns the group's whole rule list. */
export interface PhotoListHighlightRulesPayload {
  highlight_source: PhotoListHighlightSource
  highlight_rules: PhotoListHighlightRule[]
}

/** "this_file" = a rule on this store's file only; "every_store" = the
 * All-files rule. A store-file rule wins over the every-store one. */
export type PhotoListRuleScope = "this_file" | "every_store"

/** One location text a run saw in one store's file, and the rule that covers
 * it now — the rule fields are all null when no rule does. */
export interface PhotoListStargardSeenText {
  raw: string
  cars: number
  alias_id: number | null
  lot_id: number | null
  lot_name: string | null
  rule_scope: PhotoListRuleScope | null
  /** Other files whose text this same rule translated on that run. */
  also_used_by: Array<{ file: string; cars: number }>
}

export interface PhotoListStargardSeenFile {
  file: string
  cars: number
  rules: PhotoListStargardSeenText[]
  no_rule: PhotoListStargardSeenText[]
}

export interface PhotoListStargardSeen {
  run_id: number
  run_started_at: string
  /** false = the run predates the full capture: only its unrecognized texts
   * are known, so "rules not seen" is really "all rules". */
  complete: boolean
  files: PhotoListStargardSeenFile[]
  rules_not_seen: PhotoListStargardAlias[]
}

export interface PhotoListSettingsSeedCounts {
  lots_created: number
  aliases_created: number
  recon_steps_created: number
  new_lot_priorities_created: number
  used_lot_boosts_created: number
  dead_boosts_skipped: number
}

/** `includeStargardSeen`: the settings page's Address Aliases layout. The
 * photo list pages need only colors and order, so they skip building it. */
export async function fetchPhotoListSettings(
  dealerGroupId?: number | null,
  options: { includeStargardSeen?: boolean } = {},
): Promise<PhotoListSettings> {
  const params = new URLSearchParams()
  if (dealerGroupId != null) params.set("dealer_group_id", String(dealerGroupId))
  if (options.includeStargardSeen) params.set("include_stargard_seen", "true")
  const query = params.toString()
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.base}${query ? `?${query}` : ""}`,
  )
  return res.json()
}

export async function seedPhotoListSettings(
  dealerGroupId?: number | null,
): Promise<PhotoListSettingsSeedCounts> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.seedDefaults}${groupParam(dealerGroupId)}`,
    { method: "POST" },
  )
  return res.json()
}

export async function createPhotoListLot(
  name: string,
  dealerGroupId?: number | null,
): Promise<PhotoListLot> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.lots}${groupParam(dealerGroupId)}`,
    { method: "POST", body: JSON.stringify({ name }) },
  )
  return res.json()
}

export async function updatePhotoListLot(
  lotId: number,
  updates: { name?: string },
  dealerGroupId?: number | null,
): Promise<PhotoListLot & { code_references_may_break?: boolean }> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.lot(lotId)}${groupParam(dealerGroupId)}`,
    { method: "PATCH", body: JSON.stringify(updates) },
  )
  return res.json()
}

export async function deletePhotoListLot(
  lotId: number,
  dealerGroupId?: number | null,
): Promise<void> {
  await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.lot(lotId)}${groupParam(dealerGroupId)}`,
    { method: "DELETE" },
  )
}

export async function createStargardAlias(
  rawText: string,
  lotId: number,
  stargardRooftop: string,
  dealerGroupId?: number | null,
): Promise<PhotoListStargardAlias> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.stargardAliases}${groupParam(dealerGroupId)}`,
    {
      method: "POST",
      body: JSON.stringify({
        raw_text: rawText,
        lot_id: lotId,
        stargard_rooftop: stargardRooftop,
      }),
    },
  )
  return res.json()
}

export async function updateStargardAlias(
  aliasId: number,
  updates: { raw_text?: string; lot_id?: number; stargard_rooftop?: string },
  dealerGroupId?: number | null,
): Promise<PhotoListStargardAlias> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.stargardAlias(aliasId)}${groupParam(dealerGroupId)}`,
    { method: "PATCH", body: JSON.stringify(updates) },
  )
  return res.json()
}

export async function deleteStargardAlias(
  aliasId: number,
  dealerGroupId?: number | null,
): Promise<void> {
  await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.stargardAlias(aliasId)}${groupParam(dealerGroupId)}`,
    { method: "DELETE" },
  )
}

export async function createReconStep(
  step: {
    name: string
    highlight_color?: PhotoListHighlightColor | null
    priority_score?: number
    excluded_from_list?: boolean
  },
  dealerGroupId?: number | null,
): Promise<PhotoListReconStep> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.reconSteps}${groupParam(dealerGroupId)}`,
    { method: "POST", body: JSON.stringify(step) },
  )
  return res.json()
}

export async function updateReconStep(
  stepId: number,
  updates: {
    name?: string
    highlight_color?: PhotoListHighlightColor | null
    priority_score?: number
    excluded_from_list?: boolean
  },
  dealerGroupId?: number | null,
): Promise<PhotoListReconStep> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.reconStep(stepId)}${groupParam(dealerGroupId)}`,
    { method: "PATCH", body: JSON.stringify(updates) },
  )
  return res.json()
}

export async function deleteReconStep(
  stepId: number,
  dealerGroupId?: number | null,
): Promise<void> {
  await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.reconStep(stepId)}${groupParam(dealerGroupId)}`,
    { method: "DELETE" },
  )
}

export async function createNewLotPriority(
  row: { rooftop_name: string; lot_id: number; score?: number },
  dealerGroupId?: number | null,
): Promise<PhotoListNewLotPriority> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.newLotPriorities}${groupParam(dealerGroupId)}`,
    { method: "POST", body: JSON.stringify(row) },
  )
  return res.json()
}

export async function updateNewLotPriority(
  rowId: number,
  updates: { score?: number },
  dealerGroupId?: number | null,
): Promise<PhotoListNewLotPriority> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.newLotPriority(rowId)}${groupParam(dealerGroupId)}`,
    { method: "PATCH", body: JSON.stringify(updates) },
  )
  return res.json()
}

export async function deleteNewLotPriority(
  rowId: number,
  dealerGroupId?: number | null,
): Promise<void> {
  await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.newLotPriority(rowId)}${groupParam(dealerGroupId)}`,
    { method: "DELETE" },
  )
}

export async function createUsedLotBoost(
  row: { rooftop_name: string; lot_id: number; boost?: number },
  dealerGroupId?: number | null,
): Promise<PhotoListUsedLotBoost> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.usedLotBoosts}${groupParam(dealerGroupId)}`,
    { method: "POST", body: JSON.stringify(row) },
  )
  return res.json()
}

export async function updateUsedLotBoost(
  rowId: number,
  updates: { boost?: number },
  dealerGroupId?: number | null,
): Promise<PhotoListUsedLotBoost> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.usedLotBoost(rowId)}${groupParam(dealerGroupId)}`,
    { method: "PATCH", body: JSON.stringify(updates) },
  )
  return res.json()
}

export async function deleteUsedLotBoost(
  rowId: number,
  dealerGroupId?: number | null,
): Promise<void> {
  await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.usedLotBoost(rowId)}${groupParam(dealerGroupId)}`,
    { method: "DELETE" },
  )
}

export async function createHighlightRule(
  rule: PhotoListHighlightRuleInput,
  dealerGroupId?: number | null,
): Promise<PhotoListHighlightRulesPayload> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.highlightRules}${groupParam(dealerGroupId)}`,
    { method: "POST", body: JSON.stringify(rule) },
  )
  return res.json()
}

export async function updateHighlightRule(
  ruleId: number,
  updates: Partial<PhotoListHighlightRuleInput>,
  dealerGroupId?: number | null,
): Promise<PhotoListHighlightRulesPayload> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.highlightRule(ruleId)}${groupParam(dealerGroupId)}`,
    { method: "PATCH", body: JSON.stringify(updates) },
  )
  return res.json()
}

export async function deleteHighlightRule(
  ruleId: number,
  dealerGroupId?: number | null,
): Promise<PhotoListHighlightRulesPayload> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.highlightRule(ruleId)}${groupParam(dealerGroupId)}`,
    { method: "DELETE" },
  )
  return res.json()
}

export async function reorderHighlightRules(
  orderedIds: number[],
  dealerGroupId?: number | null,
): Promise<PhotoListHighlightRulesPayload> {
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.highlightRulesReorder}${groupParam(dealerGroupId)}`,
    { method: "POST", body: JSON.stringify({ ordered_ids: orderedIds }) },
  )
  return res.json()
}

/** Saves the starter rules as the group's own, replacing any it has. */
export async function loadStarterHighlightRules(
  dealerGroupId?: number | null,
  options: { onlyIfEmpty?: boolean } = {},
): Promise<PhotoListHighlightRulesPayload> {
  const onlyIfEmpty = options.onlyIfEmpty ? `${dealerGroupId != null ? "&" : "?"}only_if_empty=true` : ""
  const res = await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.highlightRulesLoadStarter}${groupParam(dealerGroupId)}${onlyIfEmpty}`,
    { method: "POST" },
  )
  return res.json()
}

export async function reorderReconSteps(
  orderedIds: number[],
  dealerGroupId?: number | null,
): Promise<void> {
  await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.reconStepsReorder}${groupParam(dealerGroupId)}`,
    { method: "POST", body: JSON.stringify({ ordered_ids: orderedIds }) },
  )
}

export async function reorderNewLotPriorities(
  rooftopName: string,
  orderedIds: number[],
  dealerGroupId?: number | null,
): Promise<void> {
  await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.newLotPrioritiesReorder}${groupParam(dealerGroupId)}`,
    { method: "POST", body: JSON.stringify({ rooftop_name: rooftopName, ordered_ids: orderedIds }) },
  )
}

export async function reorderUsedLotBoosts(
  rooftopName: string,
  orderedIds: number[],
  dealerGroupId?: number | null,
): Promise<void> {
  await authFetch(
    `${API.baseUrl}${API.endpoints.photoListSettings.usedLotBoostsReorder}${groupParam(dealerGroupId)}`,
    { method: "POST", body: JSON.stringify({ rooftop_name: rooftopName, ordered_ids: orderedIds }) },
  )
}
